import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { findSubroutine } from '@/util/application';
import { arityScope, boundReference } from '@/util/arity';
import { isArray, isNullOrUndefined, isObject, isString } from '@/util/typeGuards';
import { validateListArity } from './arity';
import { variableUtil } from './variable';

export const returnvariablesUtil = (): ValidationObject<Detail[] | undefined> => {
  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];

    const chosenSubStart = boundReference(configuration, details);

    if (isNullOrUndefined(chosenSubStart)) return errors;

    const chosenSubroutine = findSubroutine(application.subroutines, chosenSubStart);

    if (!chosenSubroutine) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is referring to a subroutine that doesn't exist`,
        id: nodeId
      });
      return errors;
    }

    errors.push(...validateListArity(configuration, details, nodeId, blockName, arityScope(application, metadata)));

    getValue(configuration, details)?.forEach((variable, index) => {
      // A row the subroutine declares no return type for is reported by its arity, not measured against a type.
      const returnType = chosenSubroutine.return_types[index];
      if (!returnType) return;

      const varErrors = variableUtil().validate(
        application,
        metadata,
        linkedSubroutineId,
        nodeId,
        blockName,
        configuration,
        variable
      );
      if (varErrors.length) {
        errors.push(...varErrors);
        return;
      }

      if (variable?.scope !== 'local') return;

      const subVar = application.variables.local?.[linkedSubroutineId]?.find(
        (localVariable) => localVariable.name === variable.name
      );
      const type = isString(returnType) ? returnType : returnType.type;
      if (type && subVar?.type !== type) {
        errors.push({
          severity: 'error',
          message: `Block ${blockName}: Variable ${variable.name} is of a different type to a matching variable in subroutine: ${chosenSubroutine.label}`,
          id: nodeId
        });
      }
    });

    return errors;
  };

  const getValue = (configuration: Configuration, obj: unknown): Detail[] | undefined => {
    const initVal = obj;
    if (isArray(initVal)) {
      return initVal as Detail[];
    } else if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validate,
    getValue: getValue
  };
};
